/**
 * ``useProducerRates`` — TanStack Query hook around
 * ``GET /api/query/producer/{id}/rates``.
 *
 * Stream A PR 5.3. Feeds the Entity Details panel's RECENT
 * MESSAGES section per Flow Q §"Frame 2" — "Status messages
 * (last 60 s): N" / "Detection messages (last 60 s): N" / "Raw blob
 * announcements (last 60 s): N".
 *
 * Cross-references:
 *   - REQ-UI-049 (RECENT MESSAGES section).
 *   - Flow Q §"Frame 2".
 *   - ADR-024 §4.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Role } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";

export type ProducerRates = components["schemas"]["ProducerRatesResponse"];

export const PRODUCER_RATES_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "admin",
  "support_engineer",
];

/**
 * Refetch interval. 30s — rates are 60s rolling counts; refreshing
 * every 30s gives a half-window cadence so transitions are visible
 * without thrashing the BFF.
 */
export const PRODUCER_RATES_REFETCH_MS = 30_000;

export function producerRatesQueryKey(
  producerId: string,
  windowSeconds: number,
): readonly unknown[] {
  return ["producer-rates", producerId, windowSeconds] as const;
}

export interface UseProducerRatesOptions {
  producerId: string | null;
  /** Defaults to 60 s. */
  windowSeconds?: number;
}

export function useProducerRates(
  options: UseProducerRatesOptions,
): UseQueryResult<ProducerRates, ApiError> {
  const session = useSession();
  const hasRole = session.hasAnyRole(PRODUCER_RATES_READER_ROLES);
  const windowSeconds = options.windowSeconds ?? 60;
  const enabled = options.producerId !== null && hasRole;

  return useQuery<ProducerRates, ApiError>({
    queryKey:
      options.producerId !== null
        ? producerRatesQueryKey(options.producerId, windowSeconds)
        : ["producer-rates", "disabled"],
    queryFn: async ({ signal }) => {
      if (options.producerId === null) {
        throw new ApiError(0, null, "useProducerRates: query ran without producerId");
      }
      const url =
        `/api/query/producer/${encodeURIComponent(options.producerId)}` +
        `/rates?window_seconds=${windowSeconds}`;
      const raw = await apiFetch<unknown>(url, { signal });
      if (!isProducerRates(raw)) {
        console.error(
          "[meridian/entity-details/use-producer-rates] response failed shape validation",
          raw,
        );
        throw new ApiError(500, raw, "producer-rates response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    refetchInterval: enabled ? PRODUCER_RATES_REFETCH_MS : false,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

function isProducerRates(value: unknown): value is ProducerRates {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.producer_id === "string" &&
    typeof obj.window_seconds === "number" &&
    typeof obj.status_count === "number" &&
    typeof obj.detection_count === "number" &&
    typeof obj.raw_count === "number" &&
    typeof obj.computed_at === "string"
  );
}
